import React, {useEffect, useState, useCallback} from "react";
import {useSearchParams, Link} from "react-router-dom";
import ProductCard from "../components/ProductCard";
import {getProducts, getCategories} from "../api";
import "./ProductList.css";
import {useCart} from "../context/CartContext";

const SORT_OPTIONS = [
  {label: "Newest", value: "-createdAt"},
  {label: "Price: Low to High", value: "price"},
  {label: "Price: High to Low", value: "-price"},
  {label: "Top Rated", value: "-rating"},
];

export default function ProductList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const {drawerOpen, setDrawerOpen} = useCart();

  const search = searchParams.get("search") || "";
  const category = searchParams.get("category") || "";
  const sort = searchParams.get("sort") || "-createdAt";
  const bestseller = searchParams.get("bestseller") || "";
  const page = Number(searchParams.get("page") || 1);

  const fetchProducts = useCallback(() => {
    setLoading(true);
    getProducts({search, category, sort, bestseller, page, limit: 16})
      .then((r) => {
        setProducts(r.data.products);
        setTotal(r.data.total);
        setPages(r.data.pages);
      })
      .finally(() => setLoading(false));
  }, [search, category, sort, bestseller, page]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    getCategories().then((r) => setCategories(r.data));
  }, []);

  function setParam(key, value) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("page");
    setSearchParams(next);
    window.scrollTo({top: 0, behavior: "smooth"});
  }

  function setPage(p) {
    const next = new URLSearchParams(searchParams);
    next.set("page", p);
    setSearchParams(next);
    window.scrollTo({top: 0, behavior: "smooth"});
  }

  const activeCategory = categories.find((c) => c._id === category);
  const pageTitle = search
    ? `Results for "${search}"`
    : bestseller === "true"
      ? "Bestsellers"
      : activeCategory?.name || "All Products";
  const pageSub = search
    ? `Browsing products matching your search`
    : activeCategory?.description ||
      "Thoughtfully designed pieces, made to order just for you.";

  return (
    <div className="product-list-page">
      {/* Breadcrumb + Header */}
      <div className="plp-header">
        <nav className="plp-breadcrumb">
          <Link to="/">Home</Link>
          <span>/</span>
          {activeCategory || search || bestseller ? (
            <>
              <Link to="/products">Products</Link>
              <span>/</span>
              <span className="current">{pageTitle}</span>
            </>
          ) : (
            <span className="current">Products</span>
          )}
        </nav>
        <p className="plp-eyebrow">The Collection</p>
        <h1 className="plp-title">{pageTitle}</h1>
        <p className="plp-sub">{pageSub}</p>
      </div>

      <div className="plp-layout">
        {/* Sidebar filters */}
        <aside className="plp-sidebar">
          <h3 className="sidebar-title">Categories</h3>
          <div className="filter-chips">
            <button
              className={`filter-btn ${!category ? "active" : ""}`}
              onClick={() => setParam("category", "")}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c._id}
                className={`filter-btn ${category === c._id ? "active" : ""}`}
                onClick={() => setParam("category", c._id)}
              >
                {c.name}
              </button>
            ))}
          </div>

          <h3 className="sidebar-title mt">Filter</h3>
          <label className="filter-check">
            <input
              type="checkbox"
              checked={bestseller === "true"}
              onChange={(e) =>
                setParam("bestseller", e.target.checked ? "true" : "")
              }
            />
            Bestsellers only
          </label>
        </aside>

        {/* Main */}
        <main className="plp-main">
          <div className="plp-top">
            {!loading && (
              <p className="plp-count">
                {total} {total === 1 ? "piece" : "pieces"}
              </p>
            )}
            <div className="sort-wrap">
              <span className="sort-label">Sort by</span>
              <select
                className="sort-select"
                value={sort}
                onChange={(e) => setParam("sort", e.target.value)}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="plp-grid">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="skeleton-card"
                  style={{aspectRatio: "1.1"}}
                />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="plp-empty">
              <span className="plp-empty-icon">✦</span>
              <p>No products found</p>
              <p className="plp-empty-hint">
                Try adjusting your filters or browse the full collection.
              </p>
              <button className="btn-pill solid" onClick={() => setSearchParams({})}>
                Clear filters
              </button>
            </div>
          ) : (
            <div className="plp-grid">
              {products.map((p, i) => (
                <div
                  key={p._id}
                  className="plp-grid-item"
                  style={{animationDelay: `${Math.min(i, 8) * 45}ms`}}
                >
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="pagination">
              {[...Array(pages)].map((_, i) => (
                <button
                  key={i}
                  className={`page-btn ${page === i + 1 ? "active" : ""}`}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
